/**
 * useParkingRealtime — subscribe to Phase-3 parking realtime events and
 * invalidate the relevant TanStack Query keys so list pages auto-refresh.
 *
 * Events (server-emitted on the `/realtime` namespace):
 *   - `parking:wrong_parker`           → fresh wrong-parker alert
 *   - `parking:slot_occupied`          → live slot status changed
 *   - `parking:ev_strike_escalation`   → unit crossed strike threshold
 *
 * These three event names are NOT (yet) part of the canonical
 * @hooddesk/shared-types `RealtimeEvent` union — that union is owned by the
 * API agent and will land in a follow-up shared-types PR. To unblock the
 * frontend without churning a cross-package type, we attach the raw socket
 * listeners directly here (bypassing the typed `subscribe()` helper) and
 * narrow on receipt.
 *
 * Mount once at the root of any page that needs live parking updates. The
 * hook is idempotent — multiple mounts are safe (each registers + cleans
 * up its own listeners).
 *
 * Optional `onWrongParker` callback fires once per inbound alert so the
 * Wrong-Parker page can play a chime + show a toast without coupling the
 * hook to a specific UX surface.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';

export interface WrongParkerEventPayload {
  alert_id: string;
  slot_id?: string | null;
  detected_plate?: string | null;
  ts?: string | null;
}

export interface SlotOccupiedEventPayload {
  slot_id: string;
  zone_id?: string | null;
  occupied: boolean;
}

export interface EvStrikeEscalationPayload {
  unit_id: string;
  strike_count: number;
}

export interface UseParkingRealtimeOptions {
  onWrongParker?: (e: WrongParkerEventPayload) => void;
  onSlotOccupied?: (e: SlotOccupiedEventPayload) => void;
  onEvStrikeEscalation?: (e: EvStrikeEscalationPayload) => void;
  /** Disable invalidations (e.g. when the page already streams live state). */
  invalidate?: boolean;
}

export function useParkingRealtime(opts: UseParkingRealtimeOptions = {}): void {
  const queryClient = useQueryClient();
  const { onWrongParker, onSlotOccupied, onEvStrikeEscalation, invalidate = true } = opts;

  useEffect(() => {
    const s = getSocket();

    const handleWrongParker = (payload: unknown): void => {
      if (invalidate) {
        void queryClient.invalidateQueries({ queryKey: ['parking', 'wrong-parker'] });
      }
      if (onWrongParker && payload && typeof payload === 'object') {
        onWrongParker(payload as WrongParkerEventPayload);
      }
    };

    const handleSlotOccupied = (payload: unknown): void => {
      if (invalidate) {
        void queryClient.invalidateQueries({ queryKey: ['parking', 'placements'] });
        void queryClient.invalidateQueries({ queryKey: ['parking', 'slots'] });
      }
      if (onSlotOccupied && payload && typeof payload === 'object') {
        onSlotOccupied(payload as SlotOccupiedEventPayload);
      }
    };

    const handleEvStrike = (payload: unknown): void => {
      if (invalidate) {
        void queryClient.invalidateQueries({ queryKey: ['parking', 'ev-strikes'] });
      }
      if (onEvStrikeEscalation && payload && typeof payload === 'object') {
        onEvStrikeEscalation(payload as EvStrikeEscalationPayload);
      }
    };

    s.on('parking:wrong_parker', handleWrongParker);
    s.on('parking:slot_occupied', handleSlotOccupied);
    s.on('parking:ev_strike_escalation', handleEvStrike);

    return () => {
      s.off('parking:wrong_parker', handleWrongParker);
      s.off('parking:slot_occupied', handleSlotOccupied);
      s.off('parking:ev_strike_escalation', handleEvStrike);
    };
  }, [queryClient, onWrongParker, onSlotOccupied, onEvStrikeEscalation, invalidate]);
}
